/* eslint-disable no-unused-vars */
import moment from 'moment';
import { AwsCommonFields, AwsServiceNames } from 'shared/constants/awsConstants';
import { getDashboardFilter, GROUP_BY_OPTIONS, transformKeys } from 'recommendationsNew/consts.js';
import { getFiltersForWasteChart } from 'recommendationsNew/components/heatMap/heatMapFilters/filterStates.js';
import { PAYMENT_OPTIONS, TERMS } from 'commitment/containers/spAnalyzerNew/utils/consts.js';
import { K8sCostTypes, legacyK8sCostTypeToResourceTypes } from '~/shared/constants/k8sConstants.js';
import { CostTypes, K8S_LEGACY_USAGE_MAPPING } from '~/usage/constants/costAndUsageConstants.js';
import { adjustK8SResourceTypeValue } from '~/usage/containers/CostAndUsageExplorer/helpers/stateMachine/baseCostUsageDataState.js';
import { convertObjToMap } from '~/shared/utils/apiUtil.js';

export const getK8sFiltersForUnifiedView = (costTypes = [], forAssets = false) => {
  const isK8sCostTypes = costTypes.some((ct) => Object.values(K8sCostTypes).includes(ct));
  // 4 cost types means user did not set any filter and by default we shouldnt filter by k8s resource type
  if (!isK8sCostTypes || costTypes.length === 4 || costTypes.length < 1) {
    return {};
  }
  return {
    [AwsCommonFields.K8S_RESOURCE_TYPE]: Array.from(
      new Set(costTypes.flatMap((ct) => legacyK8sCostTypeToResourceTypes[ct] || [])),
    ).map((item) => (forAssets ? { value: item, label: adjustK8SResourceTypeValue(item) } : item)),
  };
};

export const convertK8sCostTypesToCloudFilter = (costTypes = []) => {
  const isK8sCostTypes = costTypes.some((ct) => Object.values(K8sCostTypes).includes(ct));
  if (!isK8sCostTypes) {
    return costTypes;
  }
  return [CostTypes.COST, CostTypes.DISCOUNT];
};

export const costChartAlignParamsToFetchFunc =
  (isK8sUnified) => (routeParams, lastProcessTime, panelId, dashboardId) => {
    const {
      groupByLevel,
      secondaryGroupByLevel,
      groupByValue,
      whereParamsMap,
      startDate,
      endDate,
      periodGranLevel,
      filtersMap,
      currCostType,
      isShowAmortize,
      isApplyMargin,
      excludedFiltersStatusMap,
      likeFiltersStatus,
      filtersConfig,
      isNetAmortize,
      isNetUnblended,
      isPublicCost,
      isDistributed,
      isListUnitPrice,
      isSavingsCost,
      isRateUsageBased,
      carbonEmissionsUsage,
      SelectedUsageType,
    } = routeParams;
    let filters = new Map(filtersMap);
    if (isK8sUnified) {
      filters = new Map([...convertObjToMap(getK8sFiltersForUnifiedView(currCostType)), ...(filtersMap || [])]);
      filters.set(AwsCommonFields.COST_GLOBAL_TYPE, ['k8s']);
      if (K8S_LEGACY_USAGE_MAPPING[SelectedUsageType]) {
        filters.set(AwsCommonFields.QUANTITY_TYPE, [K8S_LEGACY_USAGE_MAPPING[SelectedUsageType]]);
      }
    }
    const arrangedArray = [
      groupByLevel,
      secondaryGroupByLevel,
      groupByValue,
      new Map(whereParamsMap),
      startDate,
      endDate,
      periodGranLevel,
      {
        isRateUsageBased,
        carbonEmissionsUsage,
        filtersMap: filters,
        excludedFiltersStatusMap: new Map(excludedFiltersStatusMap),
        likedFiltersStatus: likeFiltersStatus,
        filtersConfig,
      },
      currCostType,
      isShowAmortize,
      isApplyMargin,
      isNetAmortize,
      isNetUnblended,
      isPublicCost,
      isDistributed,
      isListUnitPrice,
      isSavingsCost,
      lastProcessTime,
      panelId,
      dashboardId,
    ];
    return arrangedArray;
  };

export const utilizationByServiceParamsToFetchFunc = (routeParams) => {
  const { service, startDate, endDate } = routeParams;
  const [year, startMonth] = startDate.split('-');
  const endMonth = endDate.split('-')[1];
  const serviceLabelToValue = {
    [AwsServiceNames.EC2]: 'EC2',
    [AwsServiceNames.RDS]: 'RDS',
    [AwsServiceNames.ELASTICACHE]: 'ElastiCache',
    [AwsServiceNames.REDSHIFT]: 'Redshift',
    [AwsServiceNames.ELASTICSEARCH]: 'ES',
  };
  return [serviceLabelToValue[service] || service, year, +startMonth, +endMonth];
};

export const savingOnDemandParamsToFetchFunc = (routeParams) => {
  const { startDate } = routeParams;
  const yearQuarter = moment(startDate).format('Q-YYYY');
  return [yearQuarter];
};

export const assetsParamsToFetchFunc = (isK8S, isK8sUnified) => (routeParams, _, panelId, dashboardId) => {
  const {
    selectedColumns,
    selectedMeasure,
    startDate,
    endDate,
    periodGranLevel,
    filtersMap,
    excludedFiltersStatusMap,
    currCostType,
  } = routeParams;

  const measuresMap = new Map([[selectedMeasure.value, 1]]);
  let filters = new Map(filtersMap);
  let costTypes = currCostType;

  if (isK8sUnified && isK8S) {
    filters = new Map([...convertObjToMap(getK8sFiltersForUnifiedView(currCostType, true)), ...(filtersMap || [])]);

    costTypes = convertK8sCostTypesToCloudFilter(currCostType || []);
    filters.set(AwsCommonFields.COST_GLOBAL_TYPE, [{ value: 'k8s' }]);
  }
  return [
    {
      startDate,
      endDate,
      granLevel: periodGranLevel,
      columns: selectedColumns,
      filtersMap: filters,
      excludeFiltersMap: new Map(excludedFiltersStatusMap),
      measuresMap,
      costTypes,
      isK8S: isK8S && !isK8sUnified,
      panelId,
      dashboardId,
    },
  ];
};

export const k8sCostChartAlignParamsToFetchFunc = (routeParams, lastProcessTime, panelId, dashboardId) => {
  const {
    currDataState,
    groupByLevel,
    secondaryGroupByLevel,
    groupByValue,
    whereParamsMap,
    startDate,
    endDate,
    periodGranLevel,
    filtersMap,
    currCostType,
    SelectedUsageType,
    selectedUsageTypeMetric,
    excludedFiltersStatusMap,
    likeFiltersStatus,
    isNetUnblended,
    isShowAmortizeCost,
    isNetAmortize,
    filtersConfig,
    wasteCostAllocationChecked,
    isListUnitPrice,
    isSavingsCost,
  } = routeParams;
  const arrangedArray = [
    currDataState,
    secondaryGroupByLevel,
    groupByValue,
    new Map(whereParamsMap),
    startDate,
    endDate,
    periodGranLevel,
    {
      filtersMap: new Map(filtersMap),
      excludedFiltersStatusMap: new Map(excludedFiltersStatusMap),
      likedFiltersStatus: likeFiltersStatus,
      filtersConfig,
    },
    currCostType,
    SelectedUsageType,
    selectedUsageTypeMetric,
    isNetUnblended,
    isShowAmortizeCost,
    isNetAmortize,
    isListUnitPrice,
    isSavingsCost,
    lastProcessTime,
    panelId,
    dashboardId,
    wasteCostAllocationChecked,
  ];
  return arrangedArray;
};

export const cloudSavingsChartAlignParamsToFetchFunc = (
  routeParams,
  panelLastProcessDate = null,
  uuid = null,
  dashboardId = null,
) => {
  const { recommType, filterSelectedValues, currPeriodGranLevel, startDate, endDate } = routeParams;

  const arrangedArray = [
    startDate,
    endDate,
    new Map(filterSelectedValues),
    currPeriodGranLevel,
    recommType,
    dashboardId,
  ];
  return arrangedArray;
};
export const savingOpportunitiesFetchFunc = (routeParams, panelLastProcessDate, id, dashboardId, state, filters) => {
  const { filterContext, sortBy } = state;
  const filtersPanel = transformKeys(filterContext);
  const groupBy = GROUP_BY_OPTIONS.items.TYPE_ID.id;
  const dashboardFilters = getDashboardFilter(filters);
  return { filters: filtersPanel, groupBy, sortBy, externalFilters: dashboardFilters, pageSize: 80 };
};

export const wasteReportFetchFunc = (params, panelLastProcessDate, panelId, dashboardId, state, filters) => {
  const { filterContext } = state;
  const filtersForWasteReport = getFiltersForWasteChart(transformKeys(filterContext));
  const dashboardFilters = getDashboardFilter(filters);
  return { filters: filtersForWasteReport, externalFilters: dashboardFilters };
};
export const savingsChartFetchFunc = (params, panelLastProcessDate, panelId, dashboardId, state, filters) => {
  const { filterContext } = state;
  const filtersForWasteReport = getFiltersForWasteChart(transformKeys(filterContext));
  const dashboardFilters = getDashboardFilter(filters);
  return {
    filters: filtersForWasteReport,
    externalFilters: dashboardFilters,
    dateGranularity: state?.dateGranularity || 'Daily',
  };
};
export const spAnalyzerDataFunc = (params, panelLastProcessDate, panelId, dashboardId, state, filters) => {
  const { selectedOptions, daysToCheck, filterStatusType, coverage, hourlyCommitment, excludeExpiringSPsDays } = state;
  return {
    selectedOptions,
    daysToCheck,
    filterStatusType,
    selectedPaymentOption: PAYMENT_OPTIONS,
    selectedTerm: TERMS,
    coverage,
    hourlyCommitment,
    excludeExpiringSPsDays,
  };
};

export const recommSummeryTableAlignParamsToFetchFunc = (routeParams, panelLastProcessDate) => {
  const { filterSelectedValues } = routeParams;
  const arrangedArray = [
    // check if the filters type is map, if so - convert the map into array:
    filterSelectedValues instanceof Map ? Array.from(filterSelectedValues) : filterSelectedValues,
    panelLastProcessDate,
  ];
  return arrangedArray;
};

export const prepareDataToKpiHalfPieChart = (data) => {
  const modifiedData = [];
  const { value } = data[0];
  const getColor = () => {
    if (value < 40) {
      return '#d95350';
    }
    if (value < 80) {
      return '#eea64e';
    }
    return '#449d44';
  };
  modifiedData.push({ value, fill: getColor() });
  modifiedData.push({ value: 100 - value, fill: '#f2f4f7' });
  return modifiedData;
};
